import styled, { css } from 'styled-components';

import {
  Button,
  StatItem,
  StyledSection,
  ContentContainer,
} from '@components/atoms';
import { Article, SectionHeader } from '@components/molecules';
import Rocket from '@public/icons/rocket.svg';
import { device } from '@styles/mediaQuery';
import useDevice from '@hooks/useDevice';
import { Fragment } from 'react';
import Image from 'next/image';
import { JOIN } from '@shared/constants/navigate';

function ApplyToJoin() {
  const { isMobile, isTablet, isDesktop } = useDevice();

  const imageSize = (isMobile && 282) || (isTablet && 382) || 484;
  const isPortable = isMobile || isTablet;

  const onJoinButtonClick = () => {
    window.location.href = JOIN;
  };

  return (
    <Container>
      <Section>
        <StyledDiv>
          <Teamwork>
            <SectionHeader
              title="Teamwork"
              src="icons/team.svg"
              justifyContent={isDesktop ? 'flex-start' : 'center'}
            />

            <Article
              align={isDesktop ? 'left' : 'center'}
              descriptionMaxWidth="750px"
              wrapAlign={isDesktop ? 'flex-start' : 'center'}
              description="Join our team and become part of a group dedicated to delivering exceptional value and care to our clients."
              wrapWidth="100%"
              title="Apply To Join Our Team"
            />

            {isDesktop && (
              <Fragment>
                <Button
                  height="50px"
                  width="180px"
                  bgColor="forestGreen"
                  color="white"
                  title="Apply Now"
                  Icon={Rocket}
                  onClick={onJoinButtonClick}
                />

                <Statistic>
                  <StatItem title="230+" subtitle="trailers" />
                  <StatItem title="30%" subtitle="team drivers" />
                  <StatItem title="70%" subtitle="solo drivers" />
                </Statistic>
              </Fragment>
            )}
          </Teamwork>
        </StyledDiv>

        <StyledDiv>
          <PhotoWrapper>
            <StyledImage
              $left={0}
              width={imageSize}
              height={imageSize}
              $rotate={isPortable ? undefined : -6.1}
              loading="lazy"
              alt="apply-to-join-1"
              src="/images/apply-to-join-1.webp"
              sizes="(min-width: 768px) 484px, (min-width: 650px) 300px"
            />

            <StyledImage
              width={imageSize}
              height={imageSize}
              $left={isPortable ? 50 : 80}
              $top={isPortable ? 60 : 130}
              $rotate={isPortable ? undefined : 6.1}
              loading="lazy"
              alt="apply-to-join-2"
              src="/images/apply-to-join-2.webp"
              sizes="(min-width: 768px) 484px, (min-width: 650px) 300px"
            />
          </PhotoWrapper>
        </StyledDiv>

        {!isDesktop && (
          <Fragment>
            <Statistic>
              <StatItem title="230+" subtitle="trailers" />
              <StatItem title="30%" subtitle="team drivers" />
              <StatItem title="70%" subtitle="solo drivers" />
            </Statistic>

            <Button
              margin="20px auto 0"
              height="50px"
              width="180px"
              bgColor="forestGreen"
              color="white"
              title="Apply Now"
              onClick={onJoinButtonClick}
              Icon={Rocket}
            />
          </Fragment>
        )}
      </Section>
    </Container>
  );
}

const Section = styled(StyledSection)`
  background-image: url('/images/backround-image.webp');
  background-repeat: no-repeat;
  background-size: contain;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 0 20px;

  ${device.laptopL(css`
    max-width: 1920px;
    flex-direction: row;
  `)}
`;

const StyledDiv = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;

  ${device.laptopL(css`
    width: 49%;
  `)}
`;

const Statistic = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;

  ${device.mobileL(css`
    max-width: 450px;
    margin: 50px auto 0;
  `)}

  ${device.tablet(css`
    max-width: 450px;
    margin: 0 auto;
  `)}
  
  ${device.laptopS(css`
    max-width: 450px;
    margin: 70px auto 0;
  `)}

  ${device.laptopL(css`
    padding: 0 40px 0 0;
    width: 522px;
    margin: 0;
  `)}
`;

const Teamwork = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-top: 50px;
  width: 100%;

  ${device.laptopL(css`
    margin-top: 150px;
    align-items: flex-start;
    height: 415px;
    width: 550px;
  `)}
`;

const PhotoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;

  width: 350px;
  height: 350px;

  ${device.mobileL(css`
    width: 400px;
    height: 400px;
  `)}

  ${device.tablet(css`
    width: 450px;
    height: 450px;
  `)}

  ${device.laptopS(css`
    width: 500px;
    height: 500px;
  `)}
  
  ${device.laptopL(css`
    width: 580px;
    height: 570px;
  `)}
`;

const Container = styled(ContentContainer)`
  overflow: hidden;
`;

const StyledImage = styled(Image)<{
  $top?: number;
  $left?: number;
  $rotate?: number;
}>`
  position: absolute;

  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  transform: ${({ $rotate }) => $rotate && `rotate(${$rotate}deg)`};

  ${device.tablet(css<{ $top?: number; $left?: number }>`
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
  `)}
`;

export default ApplyToJoin;
